import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import TopicTag from 'components/UI/TopicTag';
import { type FC, useState } from 'react';
import type { PlaygroundFunction } from 'services/api/playground.api';
import { NAVY } from 'styles/tokens';

import { functionsToRawJson, rawJsonToFunctions } from '../utils/turnMapping';
import RawJsonPanel from './RawJsonPanel';

interface ToolsEditorProps {
  functions: PlaygroundFunction[];
  onSetFunctions: (functions: PlaygroundFunction[]) => void;
  onError: (hasError: boolean) => void;
}

/** Collapsible tools editor. Functions are JSON schemas, so editing is raw JSON with a name summary. */
const ToolsEditor: FC<ToolsEditorProps> = ({ functions, onSetFunctions, onError }) => {
  const [open, setOpen] = useState(false);
  const names = functions.map((f) => f.function?.name).filter(Boolean) as string[];

  return (
    <Box>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', px: 0.5 }}
        onClick={() => setOpen((v) => !v)}
      >
        <IconButton size="small" sx={{ p: 0.25, color: alpha(NAVY, 0.6) }}>
          <ExpandMore
            sx={{ fontSize: 18, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
          />
        </IconButton>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: 13, color: NAVY }}>
          Herramientas ({functions.length})
        </Typography>
        {!open && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', ml: 0.5 }}>
            {names.slice(0, 4).map((name) => (
              <TopicTag key={name} topicName={name} color={NAVY} variant="filled" />
            ))}
            {names.length > 4 && (
              <Typography sx={{ fontSize: 11, color: alpha(NAVY, 0.5), fontFamily: 'Jost, sans-serif' }}>
                +{names.length - 4}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          <RawJsonPanel<PlaygroundFunction[]>
            value={functionsToRawJson(functions)}
            parse={rawJsonToFunctions}
            onParsed={onSetFunctions}
            onError={onError}
            minRows={8}
            ariaLabel="Herramientas en JSON"
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default ToolsEditor;
